import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Calendar, Check } from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Task {
  id: string;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  due_date: string | null;
  created_at: string;
}

interface TasksTabProps {
  tasks: Task[];
  onAddTask: () => void;
  onTaskUpdate?: () => void;
}

export const TasksTab = ({ tasks, onAddTask, onTaskUpdate }: TasksTabProps) => {
  const columns = ["pending", "in_progress", "completed"];
  
  const getTasksByStatus = (status: string) => {
    return tasks.filter((task) => task.status === status);
  };

  const handleToggleTask = async (taskId: string, currentStatus: string) => {
    const newStatus = currentStatus === "completed" ? "pending" : "completed";
    const completedAt = newStatus === "completed" ? new Date().toISOString() : null;

    const { error } = await supabase
      .from("tasks")
      .update({ 
        status: newStatus,
        completed_at: completedAt,
        updated_at: new Date().toISOString()
      })
      .eq("id", taskId);

    if (error) {
      toast.error("Failed to update task");
      console.error(error);
      return;
    }

    if (newStatus === "completed") {
      toast.success("Task completed! 🎉", {
        description: "Great job keeping the project moving forward"
      });
    } else {
      toast.info("Task reopened");
    }

    onTaskUpdate?.();
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "bg-destructive text-destructive-foreground";
      case "medium":
        return "bg-accent text-accent-foreground";
      case "low":
        return "bg-secondary text-secondary-foreground";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "pending":
        return "To Do";
      case "in_progress":
        return "In Progress";
      case "completed":
        return "Completed";
      default:
        return status;
    }
  };

  return (
    <div className="container mx-auto px-6 py-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Tasks</h2>
        <Button onClick={onAddTask} className="gap-2">
          <Plus className="w-4 h-4" />
          Add Task
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {columns.map((status) => (
          <div key={status}>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center justify-between">
                  {getStatusLabel(status)}
                  <Badge variant="secondary" className="ml-2">
                    {getTasksByStatus(status).length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {getTasksByStatus(status).length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No tasks
                  </p>
                ) : (
                  getTasksByStatus(status).map((task) => (
                    <Card 
                      key={task.id} 
                      className={`border-border/50 transition-all ${
                        task.status === "completed" ? "bg-muted/50" : ""
                      }`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={task.status === "completed"}
                            onCheckedChange={() => handleToggleTask(task.id, task.status)}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="flex items-start justify-between mb-2">
                              <h4 className={`font-medium text-sm ${
                                task.status === "completed" ? "line-through text-muted-foreground" : ""
                              }`}>
                                {task.title}
                              </h4>
                              <Badge className={getPriorityColor(task.priority)} variant="secondary">
                                {task.priority}
                              </Badge>
                            </div>
                            {task.description && (
                              <p className={`text-xs mb-3 ${
                                task.status === "completed" 
                                  ? "text-muted-foreground/70 line-through" 
                                  : "text-muted-foreground"
                              }`}>
                                {task.description}
                              </p>
                            )}
                            {task.due_date && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Calendar className="w-3 h-3" />
                                {format(new Date(task.due_date), "MMM d, yyyy")}
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        ))}
      </div>
    </div>
  );
};
